import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendCompany,
	FrontendCompanyWithAdditionalInfomation,
} from "node-library/dist/portal";
import { OrderTableSortColumn, SortOrder } from "node-library/dist/paginations";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../requestHelper/sendRequest";
import {
	AdminViewOrderWrapperPage,
	FrontendContentPage,
	OrderStatus,
	Severity,
} from "node-library/dist/constants";
import {
	CustomDropdown,
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../../common-components/inputs";
import { Pagination, TablePagination } from "@mui/material";
import {
	getOrderSortString,
	getStatusDescriptionText,
	getStatusString,
} from "../../../../helpers/orderHelper";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { SortArrowIcon } from "../../../../common-components/svgIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { YellowCrossIcon } from "../../../../common-components/toast/warnIcons";
import DiscardOrderConfirmModal from "../../../../common-components/modals/discardOrderConfirmModal";
import AdminClientOrderContent from "./adminClientOrderContent";
import OrderHistoryPageSkeletonUI from "../skeletonUI/orderHistoryPageSkeleton";

interface Props {
	user: FrontendUserInfo | undefined;
	company: FrontendCompanyWithAdditionalInfomation;
	navigateToOrderDetailsPage: (
		orderId: string,
		fromPage: FrontendContentPage
	) => void;

	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewOrdersPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { setUnsavedChanges, company } = props;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
		company.companyTestCategories && company.companyTestCategories.length > 0
			? company.companyTestCategories[0].testCategoryId
			: ""
	);

	const [orders, setOrders] = useState<Array<FrontendOrder>>([]);

	const [selectedOrder, setSelectedOrder] = useState<FrontendOrder>();

	const [loading, setLoading] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [rowsPerPage, setRowsPerPage] = useState<number>(10);

	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<OrderTableSortColumn>(
		OrderTableSortColumn.INSERTED
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [statusFilterOption, setStatusFilterOption] = useState<
		OrderStatus | ""
	>("");

	//confirm modals
	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getOrders(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/orders/get-company-submitted-orders",
				{
					testCategoryId: selectedCategoryId,

					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
					pageLimit: rowsPerPage,
					statusFilterOption,
				}
			);

			const orders: Array<FrontendOrder> = response?.data?.orders;
			const pageCount: number = response?.data?.totalPages;
			setOrders(orders);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function viewOrderCOC(order: FrontendOrder) {
		try {
			const response = await sendGETRequest("/orders/get-order-coc-url", {
				orderId: order.orderId,
			});
			window.open(response.data.signedURL, "_blank");
		} catch (error) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	const statusSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(OrderStatus).map((status: OrderStatus) => {
		const name: string = getStatusString(status, translation);
		return {
			key: status,
			name: name,
			description: "",
			value: status,
			children: (
				<div
					className={`center-text status-selection-radio-option typography-body-02 ${
						statusFilterOption === status
							? "status-selection-radio-option-selected"
							: ""
					}`}
				>
					{name}
				</div>
			),
		};
	});
	statusSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text status-selection-radio-option typography-body-02 ${
					statusFilterOption === ""
						? "status-selection-radio-option-selected"
						: ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});
	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "700px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={statusSelectionOptions}
				selectedOption={statusFilterOption}
				onChange={(e) => {
					setStatusFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(OrderTableSortColumn).map((value) => {
					return {
						value: value,
						title: getOrderSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getOrders(searchText);
	}, [
		selectedCategoryId,
		currentPagination,
		sortBy,
		sortOrder,
		statusFilterOption,
		rowsPerPage,
	]);

	async function deleteOrder(order: FrontendOrder) {
		//send a request to delete the temp order in the backend
		try {
			const response = await sendPOSTRequest("/orders/delete-order", {
				orderId: order.orderId,
			});
			//go to the submitted order page
			openSnackbar(
				translation("OrderDiscardSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />
			);
			await goBackToViewOrdersPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	function gotoOrderDetailsPage(order: FrontendOrder) {
		setSelectedOrder(order);
		props.navigateToOrderDetailsPage(
			order.orderId,
			FrontendContentPage.VIEW_ORDER_PAGE
		);
	}
	function goBackToViewOrdersPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedOrder(undefined);
		if (reload) {
			getOrders(searchText);
		}
	}

	const viewOrderTitle: React.ReactNode = (
		<div className="view-orders-page-header">
			<div className="typography-heading-01">{translation("ClientOrders")}</div>
			<CustomSearchBar
				id="view-order-page-searchbar"
				searchText={searchText}
				setSearchText={setSearchText}
				placeholder={translation("SearchByOrderNumberOrClientCompanyName")}
				searchButtonText={translation("Search")}
				onSearch={() => {
					if (currentPagination !== 1) {
						setCurrentPagination(1);
					} else {
						getOrders(searchText);
					}
				}}
			></CustomSearchBar>
		</div>
	);

	const categoryDropdownOptions: Array<{
		title: string;
		value: string;
		renderValue?: string;
	}> = company.companyTestCategories.map((category) => ({
		title: category.testCategoryName,
		value: category.testCategoryId,
	}));

	const selectCategoryDropdown: React.ReactNode = (
		<CustomDropdown
			id="category-dropdown"
			label={translation("TestCategory")}
			options={categoryDropdownOptions}
			value={selectedCategoryId}
			setValue={setSelectedCategoryId}
			displayEmpty
		/>
	);
	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						marginTop: "60px",
						minHeight: "90vh",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						{viewOrderTitle}
						{selectCategoryDropdown}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{statusSelection}
							{sortOptionSelection}
						</div>
						{loading ? (
							<OrderHistoryPageSkeletonUI />
						) : (
							<AdminClientOrderContent
								company={props.company}
								orders={orders}
								user={props.user}
								sortBy={sortBy}
								// setSortBy={setSortBy}
								sortOrder={sortOrder}
								// setSortOrder={setSortOrder}
								deleteOrder={deleteOrder}
								viewOrderCOC={viewOrderCOC}
								goToViewOrderDetails={gotoOrderDetailsPage}
								setDiscardOrderConfirmOpen={setDiscardOrderConfirmOpen}
								setProceedAction={setProceedAction}
							/>
						)}
					</div>
					{!loading && orders.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<TablePagination
								component="div"
								count={totalPageCount * rowsPerPage} // total items
								page={currentPagination - 1} // zero-indexed
								onPageChange={(event, newPage) => {
									setCurrentPagination(newPage + 1);
								}}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={(event) => {
									setRowsPerPage(parseInt(event.target.value, 10));
									setCurrentPagination(1); // reset to page 1 on change
								}}
								rowsPerPageOptions={[5, 10, 25, 50, 100]}
								sx={{
									backgroundColor: "white",
									borderRadius: "8px",
									padding: "6px 16px",
									"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
										{
											marginTop: "auto",
											marginBottom: "auto",
										},
								}}
							/>
						</div>
					)}
				</div>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{currentComponent}

			<DiscardOrderConfirmModal
				open={discardOrderConfirmOpen}
				setOpen={setDiscardOrderConfirmOpen}
				proceedAction={proceedAction}
			></DiscardOrderConfirmModal>
		</div>
	);
}
